import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import type { GCP } from "~/features/georeferencer/utils";

interface UseGeoreferenceIOProps {
  gcps: GCP[];
  setGcps: React.Dispatch<React.SetStateAction<GCP[]>>;
  imageFile: File | null;
  geotiffResult: Blob | null;
}

interface UseGeoreferenceIOResult {
  exportGcpsAsJson: () => void;
  importGcpsFromJson: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDownloadGeoTIFF: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useGeoreferenceIO = ({
  gcps,
  setGcps,
  imageFile,
  geotiffResult,
}: UseGeoreferenceIOProps): UseGeoreferenceIOResult => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ファイルをダウンロードする共通関数
  const downloadFile = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 日付文字列を生成する共通関数
  const getFormattedDateString = () =>
    new Date()
      .toISOString()
      .replace(/[-:T.Z]/g, "")
      .substring(0, 14);

  // ファイル名のベース部分を取得する共通関数
  const getFileBaseName = () => (imageFile ? imageFile.name.replace(/\.[^/.]+$/, "") : "georeferencer");

  const exportGcpsAsJson = () => {
    if (!gcps.length) {
      setError(t("feature.georeferencer.errors.noGcpsToExport"));
      return;
    }

    const jsonData = JSON.stringify(gcps, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    const dateStr = getFormattedDateString();
    const baseName = getFileBaseName();
    const fileName = `${baseName}_gcps_${dateStr}.json`;

    downloadFile(blob, fileName);
  };

  const importGcpsFromJson = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedGcps = JSON.parse(content) as GCP[];

        const isValid = importedGcps.every(
          (gcp) =>
            typeof gcp.id === "string" &&
            typeof gcp.sourceX === "number" &&
            typeof gcp.sourceY === "number" &&
            typeof gcp.targetX === "number" &&
            typeof gcp.targetY === "number",
        );

        if (!isValid) throw new Error(t("feature.georeferencer.errors.invalidGcpData"));

        const newGcps = importedGcps.map((gcp) => ({ ...gcp, id: uuidv4() }));

        setGcps(newGcps);
        setError(null);
      } catch (importError) {
        setError(
          t("feature.georeferencer.errors.gcpImportFailed", {
            error: importError instanceof Error ? importError.message : String(importError),
          }),
        );
      }

      if (fileInputRef.current) fileInputRef.current.value = "";
    };

    reader.readAsText(file);
  };

  const handleDownloadGeoTIFF = () => {
    if (!geotiffResult || !imageFile) return;

    const dateStr = getFormattedDateString();
    const baseName = getFileBaseName();
    const fileName = `${baseName}_${dateStr}_georeferenced.tif`;

    downloadFile(geotiffResult, fileName);
  };

  return {
    exportGcpsAsJson,
    importGcpsFromJson,
    handleDownloadGeoTIFF,
    fileInputRef,
    error,
    setError,
  };
};
